//@ts-check
import {getCountryCode} from '@/helpers/shadcn/utils'
import {ProductComboSteps} from '@/types/productComboTypes'
import {COUNTRY_CODES} from '@/utils/constants/countryConstants'

const COMBO_NAME_MAX_LENGTH = 255
const COMBO_LIMIT_MIN = 1
export const COMBO_LIMIT_MAX = 99
export const PRODUCT_LIMIT_MIN = 2

/**
 * Validates Product Combo data for the provided wizard step.
 * @param {import('@/types/productComboTypes').ProductCombo} productComboData - Product Combo data to validate.
 * @param {number} step - Current Product Combo step.
 * @returns {boolean} True when the current step data is invalid.
 */
export const validateProductComboData = (productComboData, step, isNameUnique, languages) => {
  switch (step) {
    case ProductComboSteps.SETUP:
      return isStepSetupInvalid(productComboData, isNameUnique)
    case ProductComboSteps.DISPLAY:
      return isStepDisplayInvalid(productComboData, languages)
    case ProductComboSteps.PRODUCTS:
      return isStepProductsInvalid(productComboData, languages)
    default:
      return false
  }
}

const isEmpty = val => val == null || (typeof val === 'string' && !val.trim())
const INTEGER_INPUT_PATTERN = /^[+-]?\d+$/

const isStrictIntegerValue = value => {
  if (typeof value === 'number') {
    return Number.isInteger(value)
  }

  if (typeof value === 'string') {
    const trimmedValue = value.trim()

    return INTEGER_INPUT_PATTERN.test(trimmedValue)
  }

  return false
}

/**
 * @typedef {Object} ProductComboSetupErrors
 * @property {string} name - Combo name validation message.
 * @property {string} comboLimit - Combo limit validation message.
 * @property {string} productLimit - Product limit validation message.
 * @property {string} routeTypeId - Route type validation message.
 * @property {string} segmentConfiguration - Segment configuration validation message.
 * @property {string} excludedSegmentConfiguration - Excluded segment configuration validation message.
 */

/**
 * Returns validation errors for Product Combo setup fields.
 * @param {Partial<import('@/types/productComboTypes').ProductCombo>} productComboData - Product Combo data to validate.
 * @param {boolean} isNameUnique - Indicates if the combo name is unique.
 * @returns {ProductComboSetupErrors} Setup field errors.
 */
export const getProductComboSetupErrors = (productComboData, isNameUnique) => {
  const name = productComboData.name?.trim() ?? ''
  const comboLimitError = isNumberWithinRange(productComboData.comboLimit, COMBO_LIMIT_MIN, COMBO_LIMIT_MAX)
  const errors = {
    name: '',
    comboLimit: '',
    productLimit: '',
    routeTypeId: '',
    segmentConfiguration: '',
    excludedSegmentConfiguration: '',
  }

  if (!name) {
    errors.name = 'Combo name is required'
  } else if (name.length > COMBO_NAME_MAX_LENGTH) {
    errors.name = `Combo name must be ${COMBO_NAME_MAX_LENGTH} characters or fewer`
  } else if (!isNameUnique) {
    errors.name = 'Combo name must be unique'
  }

  if (comboLimitError) {
    errors.comboLimit = comboLimitError
  }

  const countryCode = getCountryCode()
  const isUS = countryCode === COUNTRY_CODES.US
  const routeFieldValue = isUS ? productComboData?.businessGroup : productComboData?.routeTypeId
  const routeFieldErrorMessage = isUS ? 'Snacks and beverages is required' : 'Route type is required'

  if (isEmpty(routeFieldValue)) {
    errors.routeTypeId = routeFieldErrorMessage
  }

  if (
    !productComboData?.allCustomers &&
    (!Array.isArray(productComboData?.segmentConfiguration?.includedSegments) ||
      productComboData.segmentConfiguration?.includedSegments?.length === 0)
  ) {
    errors.segmentConfiguration = 'At least one included segment is required'
  }

  if (
    productComboData?.excludeSegment &&
    (!Array.isArray(productComboData?.segmentConfiguration?.excludedSegment?.segments) ||
      productComboData.segmentConfiguration?.excludedSegment?.segments?.length === 0)
  ) {
    errors.excludedSegmentConfiguration = 'At least one excluded segment is required'
  }

  return errors
}

/** @type {(productComboData: Partial<import('@/types/productComboTypes').ProductCombo>, isNameUnique: boolean) => boolean} */
const isStepSetupInvalid = (productComboData, isNameUnique) => {
  const setupErrors = getProductComboSetupErrors(productComboData, isNameUnique)

  return Object.values(setupErrors).some(Boolean)
}

/**
 * Validates that a numeric value is within the accepted range.
 * @param {number|string|null|undefined} value - Value to validate.
 * @param {number} min - Minimum accepted value.
 * @param {number} max - Maximum accepted value.
 * @returns {string} Validation error message, or an empty string when valid.
 */
export const isNumberWithinRange = (value, min, max) => {
  if (value === '' || value === null || value === undefined) {
    return `Limit must be between ${min} and ${max}`
  }

  if (!isStrictIntegerValue(value)) {
    return 'Limit must be a whole number'
  }

  const parsedValue = Number(typeof value === 'string' ? value.trim() : value)

  if (parsedValue < min || parsedValue > max) {
    return `Limit must be between ${min} and ${max}`
  }

  return ''
}

/** @type {(productComboData: Partial<import('@/types/productComboTypes').ProductCombo>, languages?: {code: string}[]) => boolean} */
const isStepDisplayInvalid = (productComboData, languages) => {
  if (isEmpty(productComboData?.imageUrl)) return true

  if (isNumberWithinRange(productComboData.productLimit, PRODUCT_LIMIT_MIN, COMBO_LIMIT_MAX)) {
    return true
  }

  const isTextComplete = lang => {
    return (
      productComboData?.comboSubtitle?.[lang] &&
      productComboData?.comboDetails?.[lang] &&
      productComboData?.comboDescription?.[lang]
    )
  }

  const langCodes = languages?.length ? languages.map(l => l.code) : []

  return !langCodes.every(isTextComplete)
}

/** @type {(productComboData: Partial<import('@/types/productComboTypes').ProductCombo>, languages?: {code: string}[]) => boolean} */
const isStepProductsInvalid = (productComboData, languages) => {
  const comboSkuCount = Array.isArray(productComboData.comboSkus) ? productComboData.comboSkus.length : 0
  const productLimit = Number(productComboData?.productLimit)

  if (comboSkuCount < PRODUCT_LIMIT_MIN) {
    return true
  }

  if (Number.isInteger(productLimit) && comboSkuCount > productLimit) {
    return true
  }

  if (!Array.isArray(productComboData.displayCategoryIds) || productComboData.displayCategoryIds.length === 0) {
    return true
  }

  return getProductComboSubCategoryErrors(productComboData, languages).some(subCategoryError => {
    return subCategoryError.code || Object.values(subCategoryError.values).some(Boolean)
  })
}

/**
 * Returns validation errors for optional Product Combo subcategory rows.
 * @param {Partial<import('@/types/productComboTypes').ProductCombo>} productComboData - Product Combo data to validate.
 * @param {{code: string}[]} [languages] - Enabled language list.
 * @returns {{code: string, values: Object.<string, string>}[]} Subcategory validation errors by row.
 */
export const getProductComboSubCategoryErrors = (productComboData, languages) => {
  const subCategories = Array.isArray(productComboData?.subCategories) ? productComboData.subCategories : []
  const languageCodes = languages?.length
    ? languages.map(language => language.code)
    : Array.from(new Set(subCategories.flatMap(subCategory => Object.keys(subCategory?.values || {}))))

  return subCategories.map(subCategory => ({
    code: isEmpty(subCategory?.code) ? 'Subcategory ID is required' : '',
    values: languageCodes.reduce((errors, language) => {
      errors[language] = isEmpty(subCategory?.values?.[language]) ? 'Subcategory name is required' : ''

      return errors
    }, {}),
  }))
}

export const getDefaultQuantity = (multipleSellingUnits = '') => {
  const parsedQuantity = Number.parseInt(String(multipleSellingUnits ?? ''), 10)

  return Number.isInteger(parsedQuantity) && parsedQuantity > 0 ? parsedQuantity : 1
}
